import React, { useEffect, useMemo, useState } from "react";
import {
  useGetAnalytics,
  useGetTimeseries,
  useGetAnalyticsTables,
} from "../../api/analytics";
import AnalyticsTable from "./AnalyticsTable";
import {
  DateFilterSelect,
  buildArgs,
  buildProgrammeRows,
  buildDailyRows,
  buildDailyTotals,
  programmeColumns,
  dailyColumns,
} from "./shared";

/* Build the args object for the API hooks from this card's own date + shared filters */
const useArgs = (date, filters) =>
  useMemo(
    () => buildArgs(date, filters),
    [date, filters.center_ids, filters.state, filters.district],
  );

/* Per-card date that follows the page's global date filter, while still letting
   the user override this single card from its own header dropdown. Whenever the
   global date changes, every card snaps back to it. */
const useSyncedDate = (syncDate, defaultDate) => {
  const [date, setDate] = useState(syncDate ?? defaultDate);
  useEffect(() => {
    if (syncDate) setDate(syncDate);
  }, [syncDate]);
  return [date, setDate];
};

/* ----------------------- Table 1 — Programme Summary ----------------------- */
export const SummaryCard = ({ filters, register, syncDate, defaultDate = "last_30_days" }) => {
  const [date, setDate] = useSyncedDate(syncDate, defaultDate);
  const args = useArgs(date, filters);
  const { analytics, isLoading } = useGetAnalytics(args);
  const rows = useMemo(() => buildProgrammeRows(analytics), [analytics]);

  useEffect(() => {
    register("programme", {
      title: "Programme Summary",
      columns: programmeColumns,
      rows,
    });
  }, [rows, register]);

  return (
    <AnalyticsTable
      title="Programme Summary"
      subtitle="High-level numbers for the selected range and filters."
      columns={programmeColumns}
      rows={rows}
      loading={isLoading}
      fileName="Programme_Summary"
      headerExtra={<DateFilterSelect value={date} onChange={setDate} />}
    />
  );
};

/* ----------------------- Table 2 — Daily Test Volume ----------------------- */
export const DailyVolumeCard = ({ filters, register, syncDate, defaultDate = "last_30_days" }) => {
  const [date, setDate] = useSyncedDate(syncDate, defaultDate);
  const args = useArgs(date, filters);
  const { timeSeries, isLoading } = useGetTimeseries(args);
  const rows = useMemo(() => buildDailyRows(timeSeries), [timeSeries]);
  const totals = useMemo(() => buildDailyTotals(rows), [rows]);

  useEffect(() => {
    register("daily", {
      title: "Daily Test Volume & Results",
      columns: dailyColumns,
      rows,
    });
  }, [rows, register]);

  return (
    <AnalyticsTable
      title="Daily Test Volume & Results"
      subtitle="One row per day in the selected range."
      columns={dailyColumns}
      rows={rows}
      loading={isLoading}
      totalsRow={rows.length ? totals : null}
      fileName="Daily_Test_Volume"
      headerExtra={<DateFilterSelect value={date} onChange={setDate} />}
      getRowSx={(r) =>
        parseFloat(r.criticalPct) > 10 ? { backgroundColor: "rgba(211, 47, 47, 0.12)" } : undefined
      }
    />
  );
};

/* ----------- Tables 3,4,5,7,8,13 — from /analytics-page/tables ----------- */
export const DetailTableCard = ({
  filters,
  register,
  regKey,
  tableKey,
  title,
  subtitle,
  columns,
  fileName,
  getRowSx,
  syncDate,
  defaultDate = "last_30_days",
}) => {
  const [date, setDate] = useSyncedDate(syncDate, defaultDate);
  const args = useArgs(date, filters);
  const { tables, isLoading } = useGetAnalyticsTables(args);
  const rows = useMemo(() => tables?.[tableKey] || [], [tables, tableKey]);

  useEffect(() => {
    register(regKey, { title, columns, rows });
  }, [rows, register, regKey, title, columns]);

  return (
    <AnalyticsTable
      title={title}
      subtitle={subtitle}
      columns={columns}
      rows={rows}
      loading={isLoading}
      fileName={fileName}
      getRowSx={getRowSx}
      headerExtra={<DateFilterSelect value={date} onChange={setDate} />}
    />
  );
};
